import { QueryClient, QueryKey } from '@tanstack/react-query';
import { isEqual } from 'es-toolkit/compat';
import {
  Wildfire,
  WildfireCreateUpdateData,
} from 'features/wildfires/wildfire.types';
import { isKeyInObject, DeepPartial } from 'shared/types';
import { BaseCreateUpdateData, CompareConfigItem } from './useGeoEvent.types';

type RelatedGeoEvent = { id: number; geoEventType: string };

/**
 * Compare two optional strings that could be null, '' or undefined
 */
export const optionalStringsDiffer = (
  initialString?: string | null,
  editedString?: string | null,
): boolean => {
  const emptyValues = ['', undefined, null];

  if (
    emptyValues.includes(initialString) &&
    emptyValues.includes(editedString)
  ) {
    return false;
  }

  return initialString !== editedString;
};

/**
 * Compare Evac Zone Statuses between the form GeoEvent and the form fields parsed for updating
 */
export const evacZoneStatusesDiffer = (
  initialZones: Wildfire['evacZoneStatuses'] | undefined,
  editedZones: WildfireCreateUpdateData['evacZoneStatuses'] | undefined,
): boolean => {
  // If both are undefined or empty, they're equal
  if (!initialZones && !editedZones) {
    return false;
  }

  // If one is undefined and the other isn't, they differ
  if (!initialZones || !editedZones) {
    return true;
  }

  const initialZoneItems = initialZones.map((item) => {
    return {
      evacZone: { id: item.evacZone.id },
      status: item.status,
    };
  });

  return !isEqual(initialZoneItems, editedZones);
};

export const relatedGeoEventsDiffer = (
  initialGeoEvents?: RelatedGeoEvent[],
  editedGeoEvents?: RelatedGeoEvent[],
): boolean => {
  // If both are undefined or empty, they're equal
  if (!initialGeoEvents && !editedGeoEvents) {
    return false;
  }

  // If one is undefined and the other isn't, they differ
  if (!initialGeoEvents || !editedGeoEvents) {
    return true;
  }

  const initial = initialGeoEvents.map((item) => ({
    id: item.id,
    geoEventType: item.geoEventType,
  }));

  const updated = editedGeoEvents.map((item) => ({
    id: item.id,
    geoEventType: item.geoEventType,
  }));

  return !isEqual(initial, updated);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNotEqual = (a: any, b: any): boolean => !isEqual(a, b);

/**
 * Compares region id lists regardless of order.
 *
 * Missing lists (e.g. a geo event served before this field existed) count as empty.
 */
export const regionIdsDiffer = (
  initialRegionIds?: number[] | null,
  editedRegionIds?: number[] | null,
): boolean => {
  const initialIds = initialRegionIds ?? [];
  const editedIds = editedRegionIds ?? [];

  if (initialIds.length !== editedIds.length) return true;

  const initial = new Set(initialIds);
  return editedIds.some((id) => !initial.has(id));
};

/**
 * Convert the form values to the data type expected for PATCH requests.
 *
 * Compare the geoEvent to the formValues that have been parsed for a POST request.
 *
 * Returns a new object with a partial of GeoEventData or null if patch data is empty.
 */
export const constructPatch = <
  TGeoEvent extends BaseCreateUpdateData,
  TCreateUpdate extends BaseCreateUpdateData,
  TPatch extends DeepPartial<TCreateUpdate>,
>(
  original: TGeoEvent,
  modified: TCreateUpdate,
  dataFieldConfigs: Record<string, CompareConfigItem>,
  fieldConfigs: Record<string, CompareConfigItem> = {},
): TPatch | null => {
  // Common root-level fields shared across all geo event types
  const patchConfigs: Record<string, CompareConfigItem> = {
    name: {
      shouldPatch: isNotEqual,
    },
    isActive: {
      shouldPatch: isNotEqual,
    },
    notificationType: {
      shouldPatch: isNotEqual,
    },
    lat: {
      shouldPatch: isNotEqual,
    },
    lng: {
      shouldPatch: isNotEqual,
    },
    regions: {
      shouldPatch: isNotEqual,
    },
    evacZoneStatuses: {
      shouldPatch: evacZoneStatusesDiffer,
    },
    childGeoEvents: {
      shouldPatch: relatedGeoEventsDiffer,
    },
    releasePendingZoneUids: {
      shouldPatch: isNotEqual,
    },
    ...fieldConfigs,
    // These fields are never sent with PATCH
    id: { shouldPatch: () => false },
    geoEventType: { shouldPatch: () => false },
    reporterManaged: { shouldPatch: () => false },
    initialReport: { shouldPatch: () => false },
  };

  const rootPatchData = Object.keys(patchConfigs).reduce<
    Record<string, unknown>
  >((acc, key) => {
    if (!isKeyInObject(key, patchConfigs)) {
      return acc;
    }

    const { shouldPatch } = patchConfigs[key];

    if (shouldPatch(original[key], modified[key])) {
      acc[key] = modified[key];
    }

    return acc;
  }, {}) as TPatch;

  const dataPatchData = Object.keys(dataFieldConfigs).reduce<
    Record<string, unknown>
  >((acc, key) => {
    if (!isKeyInObject(key, dataFieldConfigs)) {
      return acc;
    }

    const { shouldPatch } = dataFieldConfigs[key];

    if (shouldPatch(original.data[key], modified.data[key])) {
      acc[key] = modified.data[key];
    }

    return acc;
  }, {}) as Partial<TCreateUpdate['data']>;

  const patchData: TPatch = {
    ...rootPatchData,
    ...(dataPatchData && Object.keys(dataPatchData).length
      ? { data: dataPatchData }
      : {}),
  } as TPatch;

  return Object.keys(patchData).length ? patchData : null;
};

/**
 * Invalidates all geo event related queries after a mutation.
 * This ensures the UI stays in sync with the backend.
 */
export const invalidateGeoEventQueries = async (
  queryClient: QueryClient,
  geoEventId?: number,
): Promise<void> => {
  const invalidateKeys: QueryKey[] = [
    ['active-evacuation-zones'],
    ...(geoEventId
      ? [
          ['geoEvent', geoEventId],
          ['reporterNotes', geoEventId],
        ]
      : []),
  ];

  // Remove evacuation-zones queries entirely to prevent stale cached data
  // from causing false positive "Evacuation zones have changed" alerts
  // when regions are modified in subsequent edit sessions.
  const removeKeys: QueryKey[] = [['evacuation-zones']];

  if (geoEventId) {
    // Invalidate all map data so icons, etc update for this reporter. Intentionally *not* awaited
    // since refetching ['geoEvents'] is sometimes very slow
    queryClient.invalidateQueries({ queryKey: ['geoEvents'] });
  }

  await Promise.all([
    ...invalidateKeys.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
    ...removeKeys.map((queryKey) => queryClient.removeQueries({ queryKey })),
  ]);
};
